import { Accordion } from "@base-ui/react/accordion";
import type { PayoutStatus } from "@biteasy/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDown, Filter, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/catalyst/Button";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { orpc } from "@/orpc/client";
import { cn } from "@/util/string";

const STATUS_OPTIONS: { value: PayoutStatus | ""; label: string }[] = [
	{ value: "", label: "All Statuses" },
	{ value: "pending", label: "Pending" },
	{ value: "paid", label: "Paid" },
	{ value: "failed", label: "Failed" },
];

export interface PayoutsFilters {
	partnerId?: string;
	status?: PayoutStatus;
	amountMin?: number;
	amountMax?: number;
	createdAfter?: Date;
	createdBefore?: Date;
	paidAfter?: Date;
	paidBefore?: Date;
}

interface PayoutsFiltersBarProps {
	onFiltersChange: (filters: PayoutsFilters) => void;
}

export function PayoutsFiltersBar({ onFiltersChange }: PayoutsFiltersBarProps) {
	// Local state for accordion - prevents closing on filter changes
	const [isOpen, setIsOpen] = useState(false);

	const navigate = useNavigate();
	const search = useSearch({ from: "/_app/finances/" });

	const partnerId = search.pPartner;
	const status = search.pStatus;
	const amountMin = search.pMin;
	const amountMax = search.pMax;
	const createdAfter = search.pAfter;
	const createdBefore = search.pBefore;
	const paidAfter = search.pPaidAfter;
	const paidBefore = search.pPaidBefore;

	const setFilter = (updates: Record<string, unknown>) => {
		navigate({
			to: "/finances",
			search: (prev) => ({
				...prev,
				...updates,
				tab: prev.tab as "earnings" | "payouts" | "payment_methods",
			}),
		});
	};

	// Local state for amount inputs
	const [localAmountMin, setLocalAmountMin] = useState(
		amountMin ? String(amountMin / 100) : "",
	);
	const [localAmountMax, setLocalAmountMax] = useState(
		amountMax ? String(amountMax / 100) : "",
	);

	// Fetch partners for dropdown
	const { data: partners } = useQuery(
		orpc.partners.list.queryOptions({ input: {} }),
	);

	const hasActiveFilters =
		partnerId ||
		status ||
		amountMin ||
		amountMax ||
		createdAfter ||
		createdBefore ||
		paidAfter ||
		paidBefore;

	const activeFilterCount = [
		partnerId,
		status,
		amountMin,
		amountMax,
		createdAfter,
		createdBefore,
		paidAfter,
		paidBefore,
	].filter(Boolean).length;

	useEffect(() => {
		const filters: PayoutsFilters = {};

		if (partnerId) filters.partnerId = partnerId;
		if (status) filters.status = status as PayoutStatus;
		if (amountMin) filters.amountMin = amountMin;
		if (amountMax) filters.amountMax = amountMax;
		if (createdAfter) filters.createdAfter = new Date(createdAfter);
		if (createdBefore) filters.createdBefore = new Date(createdBefore);
		if (paidAfter) filters.paidAfter = new Date(paidAfter);
		if (paidBefore) filters.paidBefore = new Date(paidBefore);

		onFiltersChange(filters);
	}, [
		partnerId,
		status,
		amountMin,
		amountMax,
		createdAfter,
		createdBefore,
		paidAfter,
		paidBefore,
		onFiltersChange,
	]);

	const handleAmountMinBlur = () => {
		const value = localAmountMin
			? Math.round(Number.parseFloat(localAmountMin) * 100)
			: undefined;
		setFilter({ pMin: value });
	};

	const handleAmountMaxBlur = () => {
		const value = localAmountMax
			? Math.round(Number.parseFloat(localAmountMax) * 100)
			: undefined;
		setFilter({ pMax: value });
	};

	const handleClearFilters = () => {
		navigate({
			to: "/finances",
			search: (prev) => ({
				...prev,
				tab: prev.tab as "earnings" | "payouts" | "payment_methods",
				pPartner: undefined,
				pStatus: undefined,
				pMin: undefined,
				pMax: undefined,
				pAfter: undefined,
				pBefore: undefined,
				pPaidAfter: undefined,
				pPaidBefore: undefined,
			}),
		});
		setLocalAmountMin("");
		setLocalAmountMax("");
	};

	return (
		<Accordion.Root
			className="mt-6"
			value={isOpen ? ["filters"] : []}
			onValueChange={(value) => setIsOpen(value.includes("filters"))}
		>
			<Accordion.Item
				value="filters"
				className="rounded-xl bg-base-200/30 ring-1 ring-base-content/5 overflow-hidden"
			>
				<Accordion.Trigger className="group flex w-full items-center justify-between gap-4 px-5 py-4 text-sm font-medium text-base-content hover:bg-base-content/5 cursor-pointer">
					<div className="flex items-center gap-2">
						<Filter className="size-4" />
						<span>Filters</span>
						{activeFilterCount > 0 && (
							<span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
								{activeFilterCount}
							</span>
						)}
					</div>
					<ChevronDown
						className={cn(
							"size-4 text-base-content/50 transition-transform duration-300",
							"group-data-panel-open:rotate-180 group-data-panel-open:text-primary",
						)}
					/>
				</Accordion.Trigger>
				<Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-300 ease-out data-starting-style:h-0 data-ending-style:h-0">
					<div className="px-5 pb-5 pt-2">
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-4">
							{/* Partner Filter */}
							<Field>
								<Label>Partner</Label>
								<Select
									value={partnerId ?? ""}
									onChange={(e) => {
										setFilter({ pPartner: e.target.value || undefined });
									}}
								>
									<option value="">All Partners</option>
									{partners?.map((partner) => (
										<option key={partner.id} value={partner.id}>
											{partner.name}
										</option>
									))}
								</Select>
							</Field>

							{/* Status Filter */}
							<Field>
								<Label>Status</Label>
								<Select
									value={status ?? ""}
									onChange={(e) => {
										setFilter({ pStatus: e.target.value || undefined });
									}}
								>
									{STATUS_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Select>
							</Field>

							{/* Created After */}
							<Field>
								<Label>Created After</Label>
								<Input
									type="date"
									value={
										createdAfter
											? new Date(createdAfter).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											pAfter: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>

							{/* Created Before */}
							<Field>
								<Label>Created Before</Label>
								<Input
									type="date"
									value={
										createdBefore
											? new Date(createdBefore).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											pBefore: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>

							{/* Paid After */}
							<Field>
								<Label>Paid After</Label>
								<Input
									type="date"
									value={
										paidAfter
											? new Date(paidAfter).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											pPaidAfter: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>

							{/* Paid Before */}
							<Field>
								<Label>Paid Before</Label>
								<Input
									type="date"
									value={
										paidBefore
											? new Date(paidBefore).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											pPaidBefore: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>

							{/* Amount Range - spans 2 columns */}
							<Field className="lg:col-span-2">
								<Label>Amount Range</Label>
								<div data-slot="control" className="flex items-center gap-2">
									<Input
										type="number"
										placeholder="Min"
										value={localAmountMin}
										onChange={(e) => setLocalAmountMin(e.target.value)}
										onBlur={handleAmountMinBlur}
										className="w-full"
									/>
									<span className="text-base-content/40">—</span>
									<Input
										type="number"
										placeholder="Max"
										value={localAmountMax}
										onChange={(e) => setLocalAmountMax(e.target.value)}
										onBlur={handleAmountMaxBlur}
										className="w-full"
									/>
								</div>
							</Field>
						</div>

						<div className="mt-4 flex justify-end">
							{hasActiveFilters && (
								<Button outline onClick={handleClearFilters}>
									<X className="size-3 mr-1" />
									Clear Filters
								</Button>
							)}
						</div>
					</div>
				</Accordion.Panel>
			</Accordion.Item>
		</Accordion.Root>
	);
}
